import { fetchPrefectureAndMunicipalityByZipCodeApi } from "@/api/services/global/constant";
import { TGetProvincesAndMunicipalitiesResDto } from "@/api/services/global/constant/dtos/get-provinces-municipalities.dto";
import {
  INVALID_BEFORE_AGE,
  INVALID_BOOLEAN,
  INVALID_CP932_CHARACTER,
  INVALID_DATE,
  INVALID_DATE_MUST_AFTER,
  INVALID_DATE_MUST_BEFORE,
  INVALID_DATE_MUST_PAST,
  INVALID_DATE_MUST_PAST_OR_TODAY,
  INVALID_EMAIL,
  INVALID_JAPAN_TEL_NUMBER,
  INVALID_LAND_LINE_TEL_NUMBER,
  INVALID_LIMIT,
  INVALID_MOBILE_TEL_NUMBER,
  INVALID_NOT_EMPTY,
  INVALID_NOT_SYMBOL,
  INVALID_NUMBER,
  INVALID_NUMBER_INTEGER,
  INVALID_NUMBER_LESS_THAN,
  INVALID_NUMBER_MAX,
  INVALID_NUMBER_MIN,
  INVALID_NUMBER_MORE_THAN,
  INVALID_NUMBER_NEGATIVE,
  INVALID_NUMBER_POSITIVE,
  INVALID_PASSWORD_STRONG,
  INVALID_SELECT_NOT_EMPTY,
  INVALID_STRING,
  INVALID_STRING_FULL_WIDTH,
  INVALID_STRING_FURIGANA,
  INVALID_STRING_HALF_WIDTH,
  INVALID_STRING_KATAKANA,
  INVALID_STRING_LATIN,
  INVALID_STRING_LENGTH,
  INVALID_STRING_MATCHES,
  INVALID_STRING_MAX,
  INVALID_STRING_MIN,
  INVALID_STRING_UPPERCASE_AND_NUMBER,
  INVALID_URL,
  INVALID_VALUE,
  INVALID_ZIP_CODE,
  INVALID_ZIP_CODE_FORMAT,
} from "@/constants/invalids";
import { PATTERN } from "@/constants/pattern";
import { TExtendFile } from "@/types/file";
import { validateCP932Characters, ValidationOptions } from "@/utils/cp932Validator";
import { dayjs, today } from "@/utils/dayjs";
import { convertCommasToNumber } from "@/utils/input";
import { replaceStr } from "@/utils/string";
import { Dayjs, isDayjs } from "dayjs";
import * as wanakana from "wanakana";
import * as yup from "yup";

interface ErrorMessage {
  code: string;
  message: string;
}

interface ZipCodeCacheEntry {
  result?: TGetProvincesAndMunicipalitiesResDto;
  error?: string;
}
const zipCodeCache = new Map<string, ZipCodeCacheEntry>();
const MAX_CACHE_SIZE = 1000;

declare module "yup" {
  interface MixedSchema {
    isDayjs(message?: string): MixedSchema<Dayjs>;
    isAfterDateOfField(refKey: string, message: { startLabel: string; endLabel: string } | string): MixedSchema<Dayjs>;
    isBeforeDateOfField(refKey: string, message: { startLabel: string; endLabel: string } | string): MixedSchema<Dayjs>;
    isBeforeAge(age: number, message?: string): MixedSchema<Dayjs>;
    isPastOrToday(message?: string): MixedSchema<Dayjs>;
    isPast(message?: string): MixedSchema<Dayjs>;
    isExtendFile(message?: string): MixedSchema<TExtendFile>;
    isCsvFile(message?: string): MixedSchema<File>;
    isSelection(): this;
  }
  interface StringSchema {
    strongPassword(message?: string): this;
    limit(length: number, message?: string): this;
    lettersOnly(message?: string): this;
    katakanaOnly(message?: string): this;
    latinsOnly(message?: string): this;
    latinAndNumber(message?: string): this;
    halfWidthOnly(message?: string): this;
    fullWidthOnly(message?: string): this;
    furiganaOnly(message?: string): this;
    notHalfWidthCharacter(message?: string): this;
    uppercaseAlphanumericCharactersOnly(message?: string): this;
    numbersOnly(message?: string): this;
    emptyToNull(): this;
    isSelection(): this;
    email(): this;
    zipCode(message?: string): this;
    isTelNumber(message?: string): this;
    autoTransformToHalfWidth(): this;
    autoTransformToFullWidth(): this;
    autoTransformToUpperCase(): this;
    isValidCP932Character(options?: ValidationOptions): this;
    notSymbol(message?: string): this;
    maxValue(value: number, message: string): this;
  }
  interface NumberSchema {
    limit(length: number, message?: string): this;
    emptyToNull(): this;
    commas(): this;
    isSelection(): this;
    autoTransformToHalfWidth(): this;
  }
}

yup.setLocale({
  mixed: {
    notType: (ctx) => {
      const { type, path } = ctx;
      switch (type) {
        case "number":
          return replaceStr(INVALID_NUMBER, { path });
        case "string":
          return replaceStr(INVALID_STRING, { path });
        case "boolean":
          return replaceStr(INVALID_BOOLEAN, { path });
        default:
          break;
      }
      return replaceStr(INVALID_VALUE, { path });
    },
    required: ({ path, spec }) => {
      const isSelection = spec?.meta?.type === "selection";
      if (isSelection) return replaceStr(INVALID_SELECT_NOT_EMPTY, { path });
      return replaceStr(INVALID_NOT_EMPTY, { path });
    },
  },
  string: {
    email: ({ path }) => replaceStr(INVALID_EMAIL, { path }),
    length: ({ path, length }) => replaceStr(INVALID_STRING_LENGTH, { path, length }),
    max: ({ path, max }) => replaceStr(INVALID_STRING_MAX, { path, max }),
    min: ({ path, min }) => replaceStr(INVALID_STRING_MIN, { path, min }),
    url: ({ path }) => replaceStr(INVALID_URL, { path }),
    matches: ({ path }) => replaceStr(INVALID_STRING_MATCHES, { path }),
  },
  number: {
    integer: ({ path }) => replaceStr(INVALID_NUMBER_INTEGER, { path }),
    positive: ({ path }) => replaceStr(INVALID_NUMBER_POSITIVE, { path }),
    negative: ({ path }) => replaceStr(INVALID_NUMBER_NEGATIVE, { path }),
    min: ({ path, min }) => replaceStr(INVALID_NUMBER_MIN, { path, min }),
    max: ({ path, max }) => replaceStr(INVALID_NUMBER_MAX, { path, max }),
    lessThan: ({ path, less }) => replaceStr(INVALID_NUMBER_LESS_THAN, { path, less }),
    moreThan: ({ path, more }) => replaceStr(INVALID_NUMBER_MORE_THAN, { path, more }),
  },
});

yup.addMethod(yup.mixed, "isDayjs", function (message) {
  return this.test("isDayjs", message || INVALID_DATE, function (value, ctx) {
    if (!value) return true;
    if (isDayjs(value)) return true;
    return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_DATE, { path: label }) });
  });
});

yup.addMethod(yup.mixed, "isBeforeAge", function (age: number, message) {
  return this.test("isBeforeAge", message || INVALID_DATE, function (value, ctx) {
    if (!value) return true;
    if (isDayjs(value) && dayjs(value).isBefore(dayjs().subtract(age, "year"))) return true;
    return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_BEFORE_AGE, { path: label, age }) });
  });
});

yup.addMethod(yup.mixed, "isAfterDateOfField", function (refKey, message) {
  let errorMsg = INVALID_VALUE;
  if (typeof message === "string") {
    errorMsg = message;
  }
  if (typeof message === "object" && "startLabel" in message && "endLabel" in message) {
    const start = message.startLabel;
    const end = message.endLabel;
    errorMsg = replaceStr(INVALID_DATE_MUST_AFTER, { start, end });
  }
  return this.test("isAfterDateOfField", errorMsg, function (value, context) {
    const refValue = context.parent[refKey];
    if (!isDayjs(value) || !isDayjs(refValue)) return true;
    return value.isAfter(refValue);
  });
});

yup.addMethod(yup.mixed, "isBeforeDateOfField", function (refKey, message) {
  let errorMsg = INVALID_VALUE;
  if (typeof message === "string") {
    errorMsg = message;
  }
  if (typeof message === "object" && "startLabel" in message && "endLabel" in message) {
    const start = message.startLabel;
    const end = message.endLabel;
    errorMsg = replaceStr(INVALID_DATE_MUST_BEFORE, { start, end });
  }
  return this.test("isBeforeDateOfField", errorMsg, function (value, context) {
    const refValue = context.parent[refKey];
    if (!isDayjs(value) || !isDayjs(refValue)) return true;
    return value.isBefore(refValue);
  });
});

yup.addMethod(yup.mixed, "isPastOrToday", function (message) {
  return this.test("isPastOrToday", message || INVALID_DATE_MUST_PAST_OR_TODAY, function (value, ctx) {
    if (!value) return true;
    if (!isDayjs(value)) return true;
    if (value.isAfter(today().add(1, "day"))) {
      return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(message || INVALID_DATE_MUST_PAST_OR_TODAY, { path: label }) });
    }
    return true;
  });
});

yup.addMethod(yup.mixed, "isPast", function () {
  return this.test("isPast", INVALID_DATE_MUST_PAST, function (value, ctx) {
    if (!value) return true;
    if (!isDayjs(value)) return true;
    if (value.isBefore(today())) {
      return true;
    }
    return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_DATE_MUST_PAST, { path: label }) });
  });
});

yup.addMethod(yup.mixed, "isExtendFile", function (message) {
  return this.test("isExtendFile", message || INVALID_VALUE, function (value, ctx) {
    if (!value) return true;
    if (typeof value === "object" && "status" in value && "id" in value) return true;
    return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_VALUE, { path: label }) });
  });
});

yup.addMethod(yup.mixed, "isCsvFile", function (message) {
  return this.test("isCsvFile", message || INVALID_VALUE, function (value, ctx) {
    if (!value) return true;
    if (value instanceof File) {
      const allowedTypes = ["text/csv"];

      if (!allowedTypes.includes(value.type)) {
        return ctx.createError({ path: ctx.path, message: INVALID_VALUE });
      }

      return true;
    }
    return ctx.createError({ path: ctx.path, message: INVALID_VALUE });
  });
});

yup.addMethod(yup.mixed, "isSelection", function () {
  return this.meta({ type: "selection" });
});

// Add custom method to Yup for strong password validation
yup.addMethod(yup.string, "strongPassword", function (message) {
  const rules = {
    minLength: 12,
    minLowercase: 1,
    minUppercase: 1,
    minNumbers: 1,
    minSpecial: 1,
  };
  return this.test("strongPassword", message || INVALID_PASSWORD_STRONG, function (value) {
    const { path, createError } = this;

    if (!value) {
      return createError({ path, message: ({ label }) => replaceStr(INVALID_NOT_EMPTY, { path: label }) });
    }

    const { minLength, minLowercase, minUppercase, minNumbers, minSpecial } = rules;

    const lowercaseCount = (value.match(/[a-z]/g) || []).length;
    const uppercaseCount = (value.match(/[A-Z]/g) || []).length;
    const numberCount = (value.match(/\d/g) || []).length;
    const specialCount = (value.match(/[`~!@#$%^&*()\[\]{}|:;'"<,>.?/\\_\-+=]/g) || []).length;
    const isValid = /^[a-zA-Z\d`~!@#$%^&*()\[\]{}|:;'"<,>.?/\\_\-+=]+$/.test(value);

    if (!isValid) {
      return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_LATIN, { path: label }) });
    }

    if (
      value.length < minLength ||
      lowercaseCount < minLowercase ||
      uppercaseCount < minUppercase ||
      numberCount < minNumbers ||
      specialCount < minSpecial
    ) {
      return createError({ path, message: INVALID_PASSWORD_STRONG });
    }
    return true;
  });
});

yup.addMethod(yup.string, "limit", function (length, message) {
  return this.test("stringLimit", message || INVALID_LIMIT, function (value, ctx) {
    if (!value) return true;
    if (value.length >= length)
      return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_LIMIT, { path: label, max: length }) });
    return true;
  });
});

yup.addMethod(yup.string, "isValidCP932Character", function (options: ValidationOptions) {
  return this.test("isValidCP932Character", INVALID_CP932_CHARACTER, function (value, ctx) {
    if (!value) return true;

    const invalidCharacters = validateCP932Characters(value.replace(/\n|\t/g, " "), options);
    if (invalidCharacters.length > 0)
      return ctx.createError({ path: ctx.path, message: () => replaceStr(INVALID_CP932_CHARACTER, { path: invalidCharacters.join("") }) });
    return true;
  });
});

yup.addMethod(yup.number, "limit", function (length, message) {
  return this.test("numberLimit", message || INVALID_LIMIT, function (value, ctx) {
    if (!value) return true;
    if (value >= length) return ctx.createError({ path: ctx.path, message: ({ label }) => replaceStr(INVALID_LIMIT, { path: label, max: length }) });
    return true;
  });
});

yup.addMethod(yup.string, "lettersOnly", function (message) {
  return this.test("lettersOnly", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;

    if (!value) return true; // Allow empty strings
    if (PATTERN.LETTER.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_MATCHES, { path: label }) });
  });
});

yup.addMethod(yup.string, "notSymbol", function (message) {
  return this.test("notSymbol", message || INVALID_NOT_SYMBOL, function (value) {
    const { path, createError } = this;

    if (!value) return true; // Allow empty strings
    if (!PATTERN.SYMBOL.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_NOT_SYMBOL, { path: label }) });
  });
});

yup.addMethod(yup.string, "numbersOnly", function (message) {
  return this.test("numbersOnly", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.NUMBER_ONLY.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_NUMBER, { path: label }) });
  });
});

yup.addMethod(yup.string, "latinsOnly", function (message) {
  return this.test("latinsOnly", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.LATIN_ONLY.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_LATIN, { path: label }) });
  });
});

yup.addMethod(yup.string, "latinAndNumber", function (message) {
  return this.test("latinAndNumber", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.LATIN_AND_NUMBERS.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_LATIN, { path: label }) });
  });
});

yup.addMethod(yup.string, "halfWidthOnly", function (message) {
  return this.test("halfWidthOnly", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.HALF_WIDTH_ONLY.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_HALF_WIDTH, { path: label }) });
  });
});

yup.addMethod(yup.string, "fullWidthOnly", function (message) {
  return this.test("fullWidthOnly", message || INVALID_STRING_FULL_WIDTH, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.FULL_WIDTH_ONLY.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_FULL_WIDTH, { path: label }) });
  });
});

yup.addMethod(yup.string, "uppercaseAlphanumericCharactersOnly", function (message) {
  return this.test("uppercaseAlphanumericCharactersOnly", message || INVALID_STRING_UPPERCASE_AND_NUMBER, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.UPPERCASE_ALPHANUMERIC_CHARACTERS_ONLY.test(value)) return true;
    return createError({
      path,
      message: ({ label }) => message || replaceStr(INVALID_STRING_UPPERCASE_AND_NUMBER, { path: label }),
    });
  });
});

yup.addMethod(yup.string, "katakanaOnly", function (message) {
  return this.test("katakanaOnly", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (PATTERN.KATAKANA_AND_SPACE.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_KATAKANA, { path: label }) });
  });
});

yup.addMethod(yup.string, "furiganaOnly", function (message) {
  return this.meta({ isFurigana: true }).test("furiganaOnly", message || INVALID_STRING_FURIGANA, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings
    for (const char of value) {
      if (char !== "\u3000" && !wanakana.isKana(char)) {
        return createError({
          path,
          message: ({ label }) => replaceStr(INVALID_STRING_FURIGANA, { path: label }),
        });
      }
    }
    return true;
  });
});

yup.addMethod(yup.string, "notHalfWidthCharacter", function (message) {
  return this.test("notHalfWidthCharacter", message || INVALID_STRING_MATCHES, function (value) {
    const { path, createError } = this;
    if (!value) return true; // Allow empty strings

    if (!PATTERN.HALF_WIDTH_CHARACTER.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_STRING_HALF_WIDTH, { path: label }) });
  });
});

yup.addMethod(yup.string, "emptyToNull", function () {
  return this.transform(function (value, originalValue) {
    if (!value) return null;
    if (typeof originalValue === "string" && originalValue.trim() === "") return null;
    return value;
  });
});

yup.addMethod(yup.number, "emptyToNull", function () {
  return this.transform(function (value, originalValue) {
    if (typeof originalValue === "string" && originalValue.trim() === "") return null;
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    return value;
  });
});

yup.addMethod(yup.number, "commas", function () {
  return this.transform(function (value, originalValue) {
    if (!originalValue?.toString()) return originalValue;
    if (typeof originalValue === "string") {
      originalValue = convertCommasToNumber(originalValue);
    }
    return !isNaN(Number(originalValue)) ? Number(originalValue) : originalValue;
  });
});

yup.addMethod(yup.number, "isSelection", function () {
  return this.meta({ type: "selection" });
});

yup.addMethod(yup.string, "isSelection", function () {
  return this.meta({ type: "selection" });
});

yup.addMethod(yup.string, "email", function (message) {
  return this.test("email", message || INVALID_EMAIL, function (value) {
    const { path, createError } = this;
    if (!value) return true;

    if (PATTERN.EMAIL.test(value)) return true;
    return createError({ path, message: ({ label }) => replaceStr(INVALID_EMAIL, { path: label }) });
  });
});

yup.addMethod(yup.string, "zipCode", function (message) {
  return this.transform(function (value, originalValue) {
    if (typeof originalValue === "string") {
      return originalValue.replace(/-/g, "");
    }
    return originalValue;
  })
    .meta({ isZipCode: true })
    .test("zipCodeFormat", message || INVALID_ZIP_CODE_FORMAT, function (value) {
      const { createError } = this;
      if (!value) return true;

      const zipCodePattern = PATTERN.ZIP_CODE;
      if (zipCodePattern.test(value)) return true;

      return createError({ path: this.path, message: message || INVALID_ZIP_CODE_FORMAT });
    })
    .test("zipCodeApi", async function (value, ctx) {
      const { createError } = ctx;
      const zipCodePattern = PATTERN.ZIP_CODE;

      if (!value || !zipCodePattern.test(value)) {
        return true;
      }

      // Check if Zipcode is in cache
      if (zipCodeCache.has(value)) {
        const cached = zipCodeCache.get(value);
        if (cached?.error) {
          return createError({ path: ctx.path, message: cached.error });
        }
        return true; // If already in the cache and no fault, return True
      }

      if (zipCodeCache.size >= MAX_CACHE_SIZE) {
        zipCodeCache.clear();
      }

      try {
        // Call API to check Zipcode
        const result = await fetchPrefectureAndMunicipalityByZipCodeApi(value);

        // Save the result to the cache
        zipCodeCache.set(value, { result });
        return true;
      } catch (e) {
        const errorMessage = (e as ErrorMessage).message || INVALID_ZIP_CODE;
        // Save Error in cache to avoid calling API with Zipcode
        zipCodeCache.set(value, { error: errorMessage });
        return createError({ path: ctx.path, message: errorMessage });
      }
    });
});

yup.addMethod(yup.string, "isTelNumber", function (message) {
  return this.test("isTelNumber", message || INVALID_JAPAN_TEL_NUMBER, function (value) {
    const { createError } = this;

    if (!value) return true;

    const withHyphenRegex = /^0(\d{1}-\d{4}-\d{4}|\d{2}-\d{3}-\d{4}|\d{3}-\d{2}-\d{4}|\d{2}-\d{4}-\d{4}|\d{2}-\d{4}-\d{3}|\d{3}-\d{3}-\d{3})$/;

    const withoutHyphenRegex = /^0(\d{9,10})$/;

    if (withHyphenRegex.test(value) || withoutHyphenRegex.test(value)) {
      return true;
    }

    return createError({ path: this.path, message: message || INVALID_JAPAN_TEL_NUMBER });
  });
});

yup.addMethod(yup.string, "isLandlineTelNumber", function (message) {
  return this.test("isLandlineTelNumber", message || INVALID_LAND_LINE_TEL_NUMBER, function (value) {
    const { createError } = this;

    if (!value) return true;

    // ReGEX checks for a fixed number format (10 digits)
    const formatRegex = /^0(\d{1}-?\d{4}-?\d{4}|\d{2}-?\d{3}-?\d{4}|\d{3}-?\d{2}-?\d{4})$/;

    // ReGEX checks 080 or 090 or 070 or 060
    const excludeRegex = /^0[56789]0/;

    // Input cleansing: Removing white space and unnecessary characters
    const cleanedPhone = value.replace(/[\s-]/g, "");

    // Check format
    if (!formatRegex.test(value)) {
      return createError({ path: this.path, message: message || INVALID_LAND_LINE_TEL_NUMBER });
    }

    // Check if the series starts with 060 or 070 or 080 or 090 no
    if (excludeRegex.test(cleanedPhone)) {
      return createError({ path: this.path, message: message || INVALID_LAND_LINE_TEL_NUMBER });
    }

    return true;
  });
});

yup.addMethod(yup.string, "isMobileTelNumber", function (message) {
  return this.test("isMobileTelNumber", message || INVALID_MOBILE_TEL_NUMBER, function (value) {
    const { createError } = this;

    if (!value) return true;

    const regex = /^0(50|60|70|80|90)-?\d{4}-?\d{4}$/;
    if (regex.test(value)) {
      return true;
    }

    return createError({ path: this.path, message: message || INVALID_MOBILE_TEL_NUMBER });
  });
});

yup.addMethod(yup.string, "autoTransformToHalfWidth", function () {
  return this.meta({ autoTransformToHalfWidth: true });
});

yup.addMethod(yup.string, "autoTransformToFullWidth", function () {
  return this.meta({ autoTransformToFullWidth: true });
});

yup.addMethod(yup.number, "autoTransformToHalfWidth", function () {
  return this.meta({ autoTransformToHalfWidth: true });
});

yup.addMethod(yup.string, "autoTransformToUpperCase", function () {
  return this.meta({ autoTransformToUpperCase: true });
});

yup.addMethod(yup.string, "maxValue", function (max, message) {
  return this.test("maxValue", message, function (value) {
    const { createError } = this;

    if (!value) return true;
    const isValid = value <= max;

    if (!isValid) {
      return createError({ path: this.path, message: message });
    }

    return true;
  });
});

export const validator = yup;
